/**
 * Страница управления источниками
 * Автор: Cursor Generator
 * Описание: CRUD операции для RSS, API и Telegram источников
 */

import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import MagneticButton from '../components/MagneticButton';
import { showToast, initToast } from '../components/Toast';
import { API_URL } from '../utils/api.js';

export default function Sources({
  allowedTypes = ['rss', 'api', 'tg'],
  defaultType = 'rss',
  customTitle = '📡 Управление источниками',
} = {}) {
  const normalizedAllowed = useMemo(() => {
    if (!Array.isArray(allowedTypes) || allowedTypes.length === 0) {
      return ['rss', 'api', 'tg'];
    }
    return allowedTypes;
  }, [allowedTypes]);

  const normalizedDefault = normalizedAllowed.includes(defaultType)
    ? defaultType
    : normalizedAllowed[0];

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: normalizedDefault,
    url_or_channel: '',
    config: {},
  });

  useEffect(() => {
    initToast();
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      const res = await axios.get(`${API_URL}/sources`);
      setSources(res.data.sources || []);
    } catch (error) {
      console.error('Ошибка при загрузке источников:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/sources`, {
        user_id: 1,
        ...formData,
      });
      setShowForm(false);
      setFormData({ type: normalizedDefault, url_or_channel: '', config: {} });
      fetchSources();
    } catch (error) {
      console.error('Ошибка при создании источника:', error);
      showToast('Ошибка при создании источника', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Удалить источник?')) return;
    try {
      await axios.delete(`${API_URL}/sources/${id}`);
      fetchSources();
    } catch (error) {
      console.error('Ошибка при удалении источника:', error);
    }
  };

  if (loading) {
    return <div className="text-center">Загрузка...</div>;
  }

  const typeLabels = {
    rss: '📡 RSS',
    api: '🔌 API',
    tg: '🤖 Telegram',
  };

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white tracking-tighter">{customTitle}</h2>
        <MagneticButton variant="primary" onClick={() => setShowForm(!showForm)}>
          ➕ Добавить источник
        </MagneticButton>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 shadow-cartoon mb-8"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Тип источника</label>
              {normalizedAllowed.length === 1 ? (
                <div className="px-4 py-2 border-2 border-dashed border-cartoon-blue/50 rounded-2xl bg-gray-50 text-gray-700">
                  {typeLabels[normalizedAllowed[0]] || normalizedAllowed[0].toUpperCase()}
                </div>
              ) : (
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-cartoon-blue rounded-2xl focus:outline-none focus:ring-2 focus:ring-cartoon-blue"
                >
                  {normalizedAllowed.map((type) => (
                    <option key={type} value={type}>
                      {typeLabels[type] || type.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                {formData.type === 'rss'
                  ? 'RSS URL'
                  : formData.type === 'api'
                  ? 'API URL'
                  : 'Telegram канал (@channel)'}
              </label>
              <input
                type="text"
                value={formData.url_or_channel}
                onChange={(e) =>
                  setFormData({ ...formData, url_or_channel: e.target.value })
                }
                className="w-full px-4 py-2 border-2 border-cartoon-blue rounded-2xl focus:outline-none focus:ring-2 focus:ring-cartoon-blue"
                placeholder={
                  formData.type === 'rss'
                    ? 'https://example.com/feed.xml'
                    : formData.type === 'api'
                    ? 'https://api.example.com/events'
                    : '@channel'
                }
                required
              />
            </div>
            <div className="flex space-x-2">
              <MagneticButton type="submit" variant="primary">
                Создать
              </MagneticButton>
              <MagneticButton
                type="button"
                variant="warning"
                onClick={() => setShowForm(false)}
              >
                Отмена
              </MagneticButton>
            </div>
          </form>
        </motion.div>
      )}

      <div className="space-y-4">
        {sources.filter((source) => normalizedAllowed.includes(source.type)).length === 0 ? (
          <div className="text-center text-gray-500 py-10 bg-white rounded-3xl shadow-inner">
            Пока нет источников выбранного типа
          </div>
        ) : (
          sources
            .filter((source) => normalizedAllowed.includes(source.type))
            .map((source) => (
              <motion.div
                key={source.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-3xl p-6 shadow-cartoon"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold">{typeLabels[source.type]}</h3>
                    <p className="text-gray-600 break-words">{source.url_or_channel}</p>
                    <p className="text-gray-500 text-sm">
                      {source.is_active ? '✅ Активен' : '⏸️ Неактивен'}
                    </p>
                  </div>
                  <MagneticButton
                    variant="warning"
                    onClick={() => handleDelete(source.id)}
                  >
                    🗑️ Удалить
                  </MagneticButton>
                </div>
              </motion.div>
            ))
        )}
      </div>
      </div>
    </div>
  );
}


